import { createUseStyles } from "react-jss";

export const MAX_CHIP_WIDTH = 240;
export const useStyles = createUseStyles({
  chip: {
    "height": "fit-content",
    "alignItems": "center",
    "border": "2px solid",
    "display": "inline-flex",
    "justifyContent": "flex-start",
    "maxWidth": `min(${MAX_CHIP_WIDTH}px, 100%)`,
    "width": "fit-content",
    "&$small": {
      "height": "21px",
      "border": "1px solid",
      "borderRadius": "16px",
      "gap": "var(--spacing-x1, 4px)",
      "padding": "var(--spacing-x05, 2px) var(--spacing-x2, 8px);",
      "&$chipWithOneIcon": {
        "minWidth": "40px",
        "&$noContent": {
          minWidth: "21px",
        },
      },
      "&$chipWithTwoIcons": {
        minWidth: "56px",
      },
    },
    "&$medium": {
      "height": "30px",
      "borderRadius": "20px",
      "gap": "var(--spacing-x1, 4px)",
      "minWidth": "44px",
      "padding": "var(--spacing-x1, 4px) var(--spacing-x3, 12px);",
      "&$noShrink": {
        minWidth: "unset",
      },
      "&$chipWithOneIcon": {
        "minWidth": "76px",
        "&$noContent": {
          minWidth: "44px",
        },
      },
      "&$chipWithTwoIcons": {
        minWidth: "100px",
      },
    },
    "&$large": {
      "height": "41px",
      "borderRadius": "20px",
      "gap": "var(--spacing-x2, 8px)",
      "minWidth": "60px",
      "padding": "var(--spacing-x2, 8px) var(--spacing-x3, 12px);",
      "&$noShrink": {
        minWidth: "unset",
      },
      "&$chipWithOneIcon": {
        "minWidth": "92px",
        "&$noContent": {
          minWidth: "68px",
        },
      },
      "&$chipWithTwoIcons": {
        minWidth: "112px",
      },
    },
    "&$accent": {
      "background": "var(--accent)",
      "borderColor": "var(--accent)",
      "color": "var(--accent-foreground)",
      "&:hover:not($disabled,$inactive):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--accent), transparent 15%)",
        background: "color-mix(in srgb, var(--accent), transparent 15%)",
      },
      "&$selected:not($disabled,$inactive)": {
        "borderColor": "color-mix(in srgb, var(--accent), var(--foreground) 40%)",
        "&:hover:not($disabled,$inactive):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--accent), transparent 30%)",
        },
      },
    },
    "&$primary": {
      "background": "var(--primary)",
      "borderColor": "var(--primary)",
      "color": "var(--primary-foreground)",
      "&$inactive": {
        color: "var(--muted-foreground)",
      },
      "&:hover:not($disabled,$inactive):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--primary), transparent 15%)",
        background: "color-mix(in srgb, var(--primary), transparent 15%)",
      },
      "&$selected:not($disabled,$inactive)": {
        "borderColor": "color-mix(in srgb, var(--primary), var(--foreground) 40%)",
        "&:hover:not($disabled,$inactive):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--primary), transparent 30%)",
        },
      },
    },
    "&$regularColor": {
      "background": "var(--secondary)",
      "borderColor": "var(--secondary)",
      "color": "var(--secondary-foreground)",
      "&$inactive": {
        color: "var(--muted-foreground)",
      },
      "&:hover:not($disabled,$inactive):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--secondary), transparent 15%)",
        background: "color-mix(in srgb, var(--secondary), transparent 15%)",
      },
      "&$selected:not($disabled,$inactive)": {
        "borderColor": "var(--secondary-foreground)",
        "&:hover:not($disabled,$inactive):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--secondary), transparent 30%)",
        },
      },
    },
    "&$light": {
      "background": "color-mix(in srgb, var(--background), var(--secondary) 40%)",
      "borderColor": "color-mix(in srgb, var(--background), var(--secondary) 40%)",
      "color": "var(--secondary-foreground)",
      "&:hover:not($disabled,$inactive):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--secondary), transparent 15%)",
        background: "color-mix(in srgb, var(--secondary), transparent 15%)",
      },
      "&$selected:not($disabled,$inactive)": {
        "borderColor": "var(--secondary-foreground)",
        "&:hover:not($disabled,$inactive):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--secondary), transparent 30%)",
        },
      },
    },
    "&$success": {
      "background": "var(--success)",
      "borderColor": "var(--success)",
      "color": "var(--success-foreground)",
      "&:hover:not($disabled):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--success), transparent 15%)",
        background: "color-mix(in srgb, var(--success), transparent 15%)",
      },
      "&$selected:not($disabled)": {
        "borderColor": "color-mix(in srgb, var(--success), var(--foreground) 40%)",
        "&:hover:not($disabled):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--success), transparent 30%)",
        },
      },
    },
    "&$danger": {
      "background": "var(--destructive)",
      "borderColor": "var(--destructive)",
      "color": "var(--destructive-foreground)",
      "&:hover:not($disabled):not($readOnly)": {
        borderColor: "color-mix(in srgb, var(--destructive), transparent 15%)",
        background: "color-mix(in srgb, var(--destructive), transparent 15%)",
      },
      "&$selected:not($disabled)": {
        "borderColor": "color-mix(in srgb, var(--destructive), var(--foreground) 40%)",
        "&:hover:not($disabled):not($readOnly)": {
          borderColor: "color-mix(in srgb, var(--destructive), transparent 30%)",
        },
      },
    },
    "&$outlined": {
      "background": "transparent",
      "borderColor": "var(--foreground)",
      "color": "var(--foreground)",
      "&:hover:not($disabled):not($readOnly)": {
        background: "color-mix(in srgb, transparent, var(--foreground) 8%)",
      },
      "&$selected:not($disabled)": {
        "borderColor": "var(--foreground)",
        "&:hover:not($disabled):not($readOnly)": {
          background: "color-mix(in srgb, transparent, var(--foreground) 8%)",
        },
      },
    },
    "&$disabled": {
      "background": "color-mix(in srgb, var(--muted), var(--secondary) 30%)",
      "borderColor": "color-mix(in srgb, var(--muted), var(--secondary) 30%)",
      "color": "var(--muted-foreground)",
      "&$selected": {
        background: "color-mix(in srgb, var(--muted), var(--secondary) 50%)",
        borderColor: "color-mix(in srgb, var(--muted), var(--secondary) 50%)",
      },
      "&$accent": {
        "background": "color-mix(in srgb, var(--muted), var(--accent) 30%)",
        "borderColor": "color-mix(in srgb, var(--muted), var(--accent) 30%)",
        "&$selected": {
          borderColor: "color-mix(in srgb, var(--muted), var(--accent) 50%)",
        },
      },
      "&$primary": {
        "background": "color-mix(in srgb, var(--muted), var(--primary) 30%)",
        "borderColor": "color-mix(in srgb, var(--muted), var(--primary) 30%)",
        "&$selected": {
          borderColor: "color-mix(in srgb, var(--muted), var(--primary) 50%)",
        },
      },
      "&$danger": {
        "background": "color-mix(in srgb, var(--muted), var(--destructive) 30%)",
        "borderColor": "color-mix(in srgb, var(--muted), var(--destructive) 30%)",
        "&$selected": {
          borderColor: "color-mix(in srgb, var(--muted), var(--destructive) 50%)",
        },
      },
      "&$success": {
        "background": "color-mix(in srgb, var(--muted), var(--success) 30%)",
        "borderColor": "color-mix(in srgb, var(--muted), var(--success) 30%)",
        "&$selected": {
          borderColor: "color-mix(in srgb, var(--muted), var(--success) 50%)",
        },
      },
    },
  },
  noOverflow: {
    "&$noOverflow&$noOverflow": {
      overflow: "unset",
    },
  },
  accentIcon: {
    color: "var(--accent-foreground)",
  },
  primaryIcon: {
    color: "var(--primary-foreground)",
  },
  regularIcon: {
    color: "var(--secondary-foreground)",
  },
  lightIcon: {
    color: "var(--secondary-foreground)",
  },
  dangerIcon: {
    color: "var(--destructive-foreground)",
  },
  successIcon: {
    color: "var(--success-foreground)",
  },
  clickable: { cursor: "pointer" },
  chipText: {
    flex: "1",
    textAlign: "center",
  },
  accent: {},
  disabled: {},
  primary: {},
  success: {},
  large: {},
  light: {},
  medium: {},
  inactive: {},
  noShrink: {},
  readOnly: {},
  danger: {},
  regularColor: {},
  selected: {},
  small: {},
  outlined: {},
  chipWithOneIcon: {},
  chipWithTwoIcons: {},
  noContent: {},
});
